import {
  NOTE_MAX_LENGTH,
  defaultPayerId,
  noteLength,
  splitOfExpense,
  toExpensePayload,
} from "./expense";
import { SHARE_MODES, equalSplit, getRemainingCents, splitProblem } from "./money";
import {
  RECURRING_INTERVALS,
  addDays,
  isDateKey,
  parseDateKey,
  startDateBounds,
} from "./recurring";

export const INTERVAL_LABELS = {
  [RECURRING_INTERVALS.weekly]: "Wöchentlich",
  [RECURRING_INTERVALS.monthly]: "Monatlich",
  [RECURRING_INTERVALS.yearly]: "Jährlich",
};

export const RECURRING_TEXTS = {
  offline: "Wiederkehrende Ausgaben kannst du nur online anlegen und ändern.",
  unavailable: "Wiederkehrende Ausgaben sind auf dem Server noch nicht eingerichtet.",
  empty:
    "Noch keine wiederkehrenden Ausgaben. Lege z. B. die Miete einmal an – sie wird dann jeden Monat automatisch eingetragen.",
  noParticipants: "Füge zuerst Personen hinzu.",
  backfill: "Ausgaben seit dem Beginn werden sofort nachgetragen.",
  shortMonths: "In kürzeren Monaten am letzten Tag.",
};

const WEEKDAYS = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const MONTHS = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const weekdayOf = (key) => {
  const { year, month, day } = parseDateKey(key);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
};

export const formatDateKey = (key) => {
  const { year, month, day } = parseDateKey(key);
  return `${day}. ${MONTHS[month - 1]} ${year}`;
};

export const describeSchedule = ({ interval, start_date: start }) => {
  if (!isDateKey(start)) return INTERVAL_LABELS[interval] ?? "";
  const { month, day } = parseDateKey(start);
  if (interval === RECURRING_INTERVALS.weekly) return `Wöchentlich am ${WEEKDAYS[weekdayOf(start)]}`;
  if (interval === RECURRING_INTERVALS.yearly) return `Jährlich am ${day}. ${MONTHS[month - 1]}`;
  return `Monatlich am ${day}.`;
};

export const needsShortMonthHint = ({ interval, start_date: start }) =>
  isDateKey(start) &&
  ((interval === RECURRING_INTERVALS.monthly && parseDateKey(start).day > 28) ||
    (interval === RECURRING_INTERVALS.yearly && start.slice(5) === "02-29"));

export const describeNext = (template, today) => {
  if (template.paused) return "Pausiert";
  if (!template.next_date) return "Beendet";
  if (template.next_date === today) return "Heute";
  if (template.next_date === addDays(today, 1)) return "Morgen";
  return `Nächste am ${formatDateKey(template.next_date)}`;
};

export const recurringStateOf = (template) => {
  if (template.paused) return "paused";
  return template.next_date ? "active" : "ended";
};

export const templateAsExpense = (template) => ({
  split_mode: template?.split_mode ?? null,
  receivers: (template?.receivers ?? []).map((receiver) => ({
    participant_id: receiver.id,
    absolute_share_in_decimals: receiver.share,
    relative_share_in_decimals: receiver.split_value ?? 0,
  })),
});

const inGroup = (participants, id) => participants.some((p) => `${p.id}` === `${id}`);

export const initialRecurringValues = (template, participants = [], me = null, today) => {
  if (!template?.id) {
    return {
      title: "",
      amount: 0,
      payer: defaultPayerId(me, participants) ?? "",
      split: {
        mode: SHARE_MODES.equal,
        receivers: equalSplit(0, participants.map((p) => ({ id: p.id }))),
      },
      tags: [],
      note: "",
      interval: RECURRING_INTERVALS.monthly,
      startDate: today,
      endDate: "",
    };
  }
  return {
    title: template.title ?? "",
    amount: Number(template.amount) || 0,
    payer: inGroup(participants, template.payer) ? template.payer : "",
    split: splitOfExpense(templateAsExpense(template)),
    tags: [...(template.tags ?? [])],
    note: template.description ?? "",
    interval: template.interval ?? RECURRING_INTERVALS.monthly,
    startDate: template.start_date ?? today,
    endDate: template.end_date ?? "",
  };
};

const NOTE_TOO_LONG = `Die Notiz darf höchstens ${NOTE_MAX_LENGTH} Zeichen haben.`;

export const validateRecurringForm = (values, { participants = [], isNew = true, today }) => {
  const { title, amount, payer, split, note, interval, startDate, endDate } = values;
  if (!title.trim()) return "Bitte gib einen Titel ein.";
  if (!amount) return "Betrag muss größer als 0 sein!";
  if (!inGroup(participants, payer)) return "Zahler muss ausgewählt werden!";
  if (split.receivers.length === 0) return "Es muss mindestens einen Empfänger geben!";
  const problem = splitProblem(split);
  if (problem) return problem;
  if (getRemainingCents(amount, split.receivers) !== 0) {
    return "Die Anteile ergeben nicht den Gesamtbetrag.";
  }
  if (noteLength(note.trim()) > NOTE_MAX_LENGTH) return NOTE_TOO_LONG;
  if (!INTERVAL_LABELS[interval]) return "Bitte wähle, wie oft sich die Ausgabe wiederholt.";
  if (!isDateKey(startDate)) return "Bitte gib einen gültigen Beginn ein.";
  const { earliest, latest } = startDateBounds(Date.parse(`${today}T12:00:00Z`));
  if (startDate > latest) return "Der Beginn darf höchstens ein Jahr in der Zukunft liegen.";
  if (isNew && startDate < earliest) return "Der Beginn darf höchstens ein Jahr zurückliegen.";
  if (endDate && !isDateKey(endDate)) return "Bitte gib ein gültiges Ende ein.";
  if (endDate && endDate < startDate) return "Das Ende darf nicht vor dem Beginn liegen.";
  return null;
};

export const startsInPast = ({ startDate }, today, isNew) =>
  isNew && isDateKey(startDate) && startDate < today;

export const toRecurringPayload = (values) => {
  const { type: _type, ...expense } = toExpensePayload({
    title: values.title.trim(),
    amount: values.amount,
    payerId: Number(values.payer),
    receivers: values.split.receivers,
    splitMode: values.split.mode,
    tags: values.tags,
  });
  return {
    ...expense,
    description: values.note.trim() || null,
    interval: values.interval,
    start_date: values.startDate,
    end_date: values.endDate || null,
  };
};

export const startDateLimits = (today, isNew) => {
  const { earliest, latest } = startDateBounds(Date.parse(`${today}T12:00:00Z`));
  return { min: isNew ? earliest : undefined, max: latest };
};
